import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { BackofficeSessionService } from '../core/backoffice-session.service';
import { BackofficeScoreReportsApiService } from '../core/backoffice-score-reports-api.service';
import { BackofficeScoreReportPayload } from '../core/backoffice-score-reports-api.types';
import { I18nService } from '../core/i18n.service';

@Component({
  selector: 'app-backoffice-score-report-detail-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, DatePipe],
  template: `
    <section class="page">
      <p><a [routerLink]="['/backoffice/tournaments', tournamentId(), 'score-reports']">{{ t('bo.reportDetail.back') }}</a></p>
      <h1>{{ t('bo.reportDetail.title') }}</h1>

      @if (loading()) {
        <p class="state">{{ t('bo.reportDetail.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else if (report(); as data) {
        <div class="card">
          <p>{{ t('bo.reportDetail.report', { id: data.id }) }} - {{ t('bo.reportDetail.status', { value: statusLabel(data.status) }) }}</p>
          <p>{{ t('bo.reportDetail.match', { id: data.match?.id || data.bracket_match_id, round: data.match?.round_number || '-', match: data.match?.match_number || '-' }) }}</p>
          <p>{{ t('bo.reportDetail.team', { team: data.reporting_team_id, user: data.reported_by_user_id }) }}</p>
          <p>{{ t('bo.reportDetail.global', { s1: data.score_team1, s2: data.score_team2 }) }}</p>
          <p>{{ t('bo.reportDetail.date', { date: (data.reported_at | date: 'short') ?? '' }) }}</p>
          @if (data.note) { <p>{{ t('bo.reportDetail.note', { value: data.note }) }}</p> }
        </div>

        <div class="card">
          <h2>{{ t('bo.reportDetail.games') }}</h2>
          @if (data.games.length === 0) {
            <p class="state">{{ t('bo.reportDetail.noGames') }}</p>
          } @else {
            <ul>
              @for (game of data.games; track $index) {
                <li>{{ t('bo.reportDetail.gameRow', { num: game.game_number, map: game.map_name || 'N/A', s1: game.team1_score, s2: game.team2_score }) }}</li>
              }
            </ul>
          }
        </div>

        <div class="card">
          <h2>{{ t('bo.reportDetail.screens') }}</h2>
          @if (data.screenshots.length === 0) {
            <p class="state">{{ t('bo.reportDetail.noScreens') }}</p>
          } @else {
            <ul>
              @for (shot of data.screenshots; track $index) {
                <li>
                  <div class="screenshot-proof">
                    <button
                      type="button"
                      class="screenshot-trigger"
                      (click)="openScreenshotPreview(shot.screenshot_url, shot.caption || ('#' + (shot.position || ($index + 1))))"
                    >
                      <img [src]="shot.screenshot_url" alt="Score proof screenshot" />
                    </button>
                  </div>
                  <div class="screenshot-actions">
                    <span>#{{ shot.position || ($index + 1) }}</span>
                    <button
                      type="button"
                      class="secondary screenshot-open"
                      (click)="openScreenshotPreview(shot.screenshot_url, shot.caption || ('#' + (shot.position || ($index + 1))))"
                    >
                      {{ t('bo.reportDetail.openPreview') }}
                    </button>
                  </div>
                  @if (shot.caption) { <span>({{ shot.caption }})</span> }
                </li>
              }
            </ul>
          }
        </div>

        @if (data.contestation.contested) {
          <div class="card card--contest">
            <h2>{{ t('bo.reportDetail.contestTitle') }}</h2>
            <p>{{ t('bo.reportDetail.contestTeam', { team: data.contestation.contesting_team_name || data.contestation.contesting_team_id || '-' }) }}</p>
            <p>{{ t('bo.reportDetail.contestUser', { user: data.contestation.contested_by_user_pseudo || data.contestation.contested_by_user_id || '-' }) }}</p>
            <p>{{ t('bo.reportDetail.contestDate', { date: (data.contestation.contested_at | date: 'short') ?? '' }) }}</p>
            @if (data.contestation.contestation_note) {
              <p>{{ t('bo.reportDetail.contestNote', { value: data.contestation.contestation_note }) }}</p>
            }
          </div>
        }

        <div class="card">
          <h2>{{ t('bo.reportDetail.decision') }}</h2>
          <label>{{ t('bo.reportDetail.newStatus') }}
            <select [(ngModel)]="targetStatus">
              <option value="in_review">in_review</option>
              <option value="validated">validated</option>
              <option value="rejected">rejected</option>
            </select>
          </label>
          <label>{{ t('bo.reportDetail.optNote') }}
            <textarea rows="2" [(ngModel)]="note"></textarea>
          </label>
          <button type="button" [disabled]="deciding()" (click)="submitDecision()">{{ t('bo.reportDetail.apply') }}</button>
          @if (decisionMessage()) {
            <p class="state state--success">{{ decisionMessage() }}</p>
          }
        </div>

        <div class="card">
          <h2>{{ t('bo.reportDetail.history') }}</h2>
          @if (!data.decisions || data.decisions.length === 0) {
            <p class="state">{{ t('bo.reportDetail.noHistory') }}</p>
          } @else {
            <ul>
              @for (decision of data.decisions; track decision.id) {
                <li>
                  {{ t('bo.reportDetail.historyRow', { from: decision.from_status, to: decision.to_status, actor: decision.actor_user_id, date: (decision.created_at | date: 'short') ?? '' }) }}
                  @if (decision.note) { <span>{{ t('bo.reportDetail.historyNote', { value: decision.note }) }}</span> }
                </li>
              }
            </ul>
          }
        </div>
      }
    </section>

    @if (previewingScreenshotUrl(); as screenshotUrl) {
      <div class="modal-backdrop" (click)="closeScreenshotPreview()">
        <div class="modal-dialog" (click)="$event.stopPropagation()">
          <div class="modal-header">
            <div class="modal-copy">
              <h2>{{ t('bo.reportDetail.previewTitle') }}</h2>
              @if (previewingScreenshotLabel()) {
                <p>{{ previewingScreenshotLabel() }}</p>
              }
            </div>
            <button type="button" class="secondary modal-close" (click)="closeScreenshotPreview()">
              {{ t('bo.reportDetail.closePreview') }}
            </button>
          </div>
          <div class="modal-content">
            <img [src]="screenshotUrl" alt="Score proof screenshot" />
          </div>
        </div>
      </div>
    }
  `,
  styles: `
    .page { padding: 1rem; max-width: 920px; }
    h1 { margin: 0 0 0.8rem; font-size: 1.35rem; color: var(--text-strong); }
    h2 { margin: 0 0 0.55rem; font-size: 1.05rem; color: var(--text-strong); }
    .card { border: 1px solid var(--line-soft); border-radius: 10px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 0.75rem; margin-bottom: 0.75rem; box-shadow: var(--shadow-soft); }
    .card--contest { border-color: rgba(236, 165, 69, 0.42); background: linear-gradient(180deg, rgba(46, 31, 12, 0.92) 0%, rgba(34, 23, 10, 0.94) 100%); }
    .card p { margin: 0.2rem 0; color: var(--text-muted); }
    .card strong { color: var(--text-strong); }
    ul { margin: 0.3rem 0 0; padding-left: 1rem; color: var(--text-muted); }
    li { margin: 0.25rem 0; }
    .screenshot-proof { margin: 0 0 0.45rem; }
    .screenshot-trigger {
      display: block;
      padding: 0;
      border: none;
      background: transparent;
      cursor: zoom-in;
    }
    .screenshot-proof img {
      display: block;
      width: min(100%, 320px);
      max-height: 220px;
      object-fit: contain;
      border-radius: 10px;
      border: 1px solid rgba(124, 157, 211, 0.28);
      background: rgba(8, 14, 25, 0.92);
    }
    .screenshot-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.55rem;
      align-items: center;
      margin-bottom: 0.25rem;
    }
    .screenshot-open { padding: 0.4rem 0.7rem; }
    label { display: grid; gap: 0.35rem; margin-bottom: 0.5rem; font-size: 0.92rem; color: #dbe9ff; }
    textarea, select { padding: 0.55rem; border: 1px solid var(--line-soft); border-radius: 8px; background: rgba(13, 21, 34, 0.9); color: var(--text-strong); }
    button { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.55rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; }
    .state { color: var(--text-muted); }
    .state--success { color: var(--ok-text); }
    .state--error { color: var(--danger); }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
    .modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 30;
      display: grid;
      place-items: center;
      padding: 1.25rem;
      background: rgba(3, 8, 17, 0.78);
      backdrop-filter: blur(4px);
    }
    .modal-dialog {
      width: min(100%, 1100px);
      max-height: calc(100vh - 2.5rem);
      border: 1px solid rgba(124, 157, 211, 0.28);
      border-radius: 16px;
      background: linear-gradient(180deg, rgba(19, 30, 49, 0.98) 0%, rgba(11, 19, 32, 0.98) 100%);
      box-shadow: 0 24px 80px rgba(0, 0, 0, 0.42);
      overflow: hidden;
    }
    .modal-header {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      align-items: start;
      padding: 1rem 1rem 0.75rem;
      border-bottom: 1px solid rgba(124, 157, 211, 0.18);
    }
    .modal-copy h2 {
      margin: 0;
      font-size: 1rem;
    }
    .modal-copy p {
      margin: 0.2rem 0 0;
      color: var(--text-muted);
    }
    .modal-close { white-space: nowrap; }
    .modal-content {
      padding: 1rem;
      overflow: auto;
      max-height: calc(100vh - 8rem);
    }
    .modal-content img {
      display: block;
      width: 100%;
      height: auto;
      max-height: calc(100vh - 10rem);
      object-fit: contain;
      border-radius: 12px;
      background: rgba(6, 11, 20, 0.96);
    }
  `
})
export class BackofficeScoreReportDetailPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly backofficeSession = inject(BackofficeSessionService);
  private readonly api = inject(BackofficeScoreReportsApiService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly tournamentId = signal(0);
  protected readonly reportId = signal(0);
  protected readonly report = signal<BackofficeScoreReportPayload | null>(null);
  protected readonly loading = signal(true);
  protected readonly deciding = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly decisionMessage = signal<string | null>(null);
  protected readonly previewingScreenshotUrl = signal<string | null>(null);
  protected readonly previewingScreenshotLabel = signal<string | null>(null);

  protected targetStatus: 'in_review' | 'validated' | 'rejected' = 'in_review';
  protected note = '';

  constructor() {
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const tournamentId = Number(params.get('tournamentId') ?? '0');
          const reportId = Number(params.get('id') ?? '0');
          this.tournamentId.set(tournamentId);
          this.reportId.set(reportId);
          return this.api.getById(tournamentId, reportId);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ score_report }) => {
          this.report.set(score_report);
          this.loading.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          if (error?.status === 403) {
            this.errorMessage.set(this.t('bo.reportDetail.forbiddenOpen'));
            this.loading.set(false);
            return;
          }
          const apiError = error?.error?.error;
          this.errorMessage.set(typeof apiError === 'string' ? apiError : this.t('bo.reportDetail.loadError'));
          this.loading.set(false);
        }
      });
  }

  protected submitDecision(): void {
    this.deciding.set(true);
    this.errorMessage.set(null);
    this.decisionMessage.set(null);

    this.api
      .decide(this.tournamentId(), this.reportId(), this.targetStatus, this.note.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ score_report }) => {
          this.report.set(score_report);
          this.decisionMessage.set(this.t('bo.reportDetail.applied', { value: this.statusLabel(this.targetStatus) }));
          this.deciding.set(false);
        },
        error: (error) => {
          if (error?.status === 401) {
            this.handleUnauthorized();
            return;
          }
          if (error?.status === 403) {
            this.errorMessage.set(this.t('bo.reportDetail.forbiddenApply'));
            this.deciding.set(false);
            return;
          }
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) ? apiErrors.join(' | ') : this.t('bo.reportDetail.decisionError'))
          );
          this.deciding.set(false);
        }
      });
  }

  protected statusLabel(status: string): string {
    if (status === 'reported') {
      return this.t('bo.reports.status.reported');
    }
    if (status === 'in_review') {
      return this.t('bo.reports.status.review');
    }
    if (status === 'validated') {
      return this.t('bo.reports.status.validated');
    }
    if (status === 'rejected') {
      return this.t('bo.reports.status.rejected');
    }
    return status;
  }

  protected openScreenshotPreview(url: string, label?: string): void {
    this.previewingScreenshotUrl.set(url);
    this.previewingScreenshotLabel.set(label ?? null);
  }

  protected closeScreenshotPreview(): void {
    this.previewingScreenshotUrl.set(null);
    this.previewingScreenshotLabel.set(null);
  }

  private handleUnauthorized(): void {
    this.backofficeSession.clearToken();
    void this.router.navigateByUrl('/backoffice/session');
  }
  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
